/**
 * @fileoverview Google Ads OAuth connect button component.
 * @module features/settings/components/GoogleAdsOAuthConnect
 */

import { type ReactElement, useState, useCallback, useEffect, useRef } from 'react'
import { Plus, Loader2 } from 'lucide-react'
import { toast } from 'sonner'
import { useTranslation } from 'react-i18next'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'

import { Button } from '@/components/ui/button'
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
} from '@/components/ui/dialog'
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select'
import { useActiveOrg } from '@/features/orgs/context/ActiveOrgProvider'
import {
  getGoogleAdsOAuthConfig,
  getGoogleAdsAuthorizationUrl,
  getGoogleAdsAccounts,
  completeGoogleAdsOAuth,
  type CustomerAccountInfo,
} from '../api/googleAdsOAuthService'
import { useAdPlatformSyncStatus } from '../hooks/useAdPlatformSyncStatus'

export interface GoogleAdsOAuthConnectProps {
  /** Callback after connection is successful */
  onConnected?: () => Promise<unknown> | unknown
  /** Custom button label (default: "Connect") */
  buttonLabel?: string
  /** Custom button icon element */
  buttonIcon?: React.ReactNode
  /** Show success toasts (default: true). Set to false for onboarding flows. */
  showSuccessToasts?: boolean
}

/** Popup window dimensions */
const POPUP_WIDTH = 600
const POPUP_HEIGHT = 700

/**
 * Formats a Google Ads customer ID with dashes (e.g., "7166258797" → "716-625-8797")
 */
function formatGoogleAdsId(customerId: string): string {
  const digits = customerId.replace(/\D/g, '')
  if (digits.length !== 10) return customerId
  return `${digits.slice(0, 3)}-${digits.slice(3, 6)}-${digits.slice(6)}`
}

/**
 * GoogleAdsOAuthConnect
 *
 * Button that initiates Google Ads OAuth flow.
 * Uses popup window for Google authorization.
 *
 * @component
 */
export function GoogleAdsOAuthConnect({ onConnected, buttonLabel, buttonIcon, showSuccessToasts = true }: GoogleAdsOAuthConnectProps): ReactElement {
  const { t } = useTranslation('features-integrations')
  const { activeOrgId } = useActiveOrg()
  const queryClient = useQueryClient()
  const { isSyncing, markConnectionComplete } = useAdPlatformSyncStatus('GOOGLE_ADS')

  const [isConnecting, setIsConnecting] = useState(false)
  const [showAccountSelector, setShowAccountSelector] = useState(false)
  const [accounts, setAccounts] = useState<CustomerAccountInfo[]>([])
  const [selectedAccountId, setSelectedAccountId] = useState<string>('')
  const popupRef = useRef<Window | null>(null)
  const pollIntervalRef = useRef<number | null>(null)
  const isConnectingRef = useRef(false)

  // Keep ref in sync with state
  useEffect(() => {
    isConnectingRef.current = isConnecting
  }, [isConnecting])

  // Fetch OAuth config to check if configured
  const configQuery = useQuery({
    queryKey: ['google-ads', 'oauth', 'config'],
    queryFn: getGoogleAdsOAuthConfig,
    staleTime: 60 * 60 * 1000, // 1 hour
  })

  // Listen for postMessage from popup and cleanup on unmount
  useEffect(() => {
    const handleMessage = async (event: MessageEvent): Promise<void> => {
      // Verify origin and message type
      if (event.data?.type !== 'google-ads-oauth-callback') return

      const { success, message } = event.data as { success: boolean; message: string }

      // Stop polling
      if (pollIntervalRef.current) {
        clearInterval(pollIntervalRef.current)
        pollIntervalRef.current = null
      }

      if (success) {
        // Fetch accounts immediately
        try {
          const response = await getGoogleAdsAccounts()
          if (response.success && response.accounts.length > 0) {
            setAccounts(response.accounts)
            setShowAccountSelector(true)
          } else {
            toast.error(
              response.error ||
                t('cards.integrations.google.noAccounts', {
                  defaultValue: 'No Google Ads accounts found',
                })
            )
          }
        } catch {
          toast.error(
            t('cards.integrations.google.error', {
              defaultValue: 'Failed to fetch Google Ads accounts',
            })
          )
        }
      } else {
        toast.error(message || t('cards.integrations.google.error', {
          defaultValue: 'Google Ads authorization failed',
        }))
      }

      setIsConnecting(false)
    }

    window.addEventListener('message', handleMessage)

    return () => {
      window.removeEventListener('message', handleMessage)
      if (pollIntervalRef.current) {
        clearInterval(pollIntervalRef.current)
      }
      if (popupRef.current && !popupRef.current.closed) {
        popupRef.current.close()
      }
    }
  }, [t])

  // Poll as fallback - postMessage is primary communication method
  const startPollingForAccounts = useCallback(() => {
    // Clear any existing polling
    if (pollIntervalRef.current) {
      clearInterval(pollIntervalRef.current)
    }

    let attempts = 0
    const maxAttempts = 60 // 2 minutes at 2 second intervals

    pollIntervalRef.current = window.setInterval(async () => {
      attempts++

      // Check if popup is closed (wrapped in try-catch for COOP policy)
      let isPopupClosed = false
      try {
        isPopupClosed = !popupRef.current || popupRef.current.closed
      } catch {
        // COOP policy blocks access - assume popup might be closed
        isPopupClosed = true
      }

      if (isPopupClosed) {
        // Popup closed - stop polling, postMessage handler will take over if auth succeeded
        if (pollIntervalRef.current) {
          clearInterval(pollIntervalRef.current)
          pollIntervalRef.current = null
        }
        // Give postMessage a moment to arrive before resetting state
        setTimeout(() => {
          // Only reset if we're still in connecting state (postMessage didn't arrive)
          if (isConnectingRef.current) {
            setIsConnecting(false)
          }
        }, 500)
      } else if (attempts >= maxAttempts) {
        // Timeout - stop polling
        if (pollIntervalRef.current) {
          clearInterval(pollIntervalRef.current)
          pollIntervalRef.current = null
        }
        try {
          popupRef.current?.close()
        } catch {
          // Ignore COOP errors
        }
        toast.error(
          t('cards.integrations.google.timeout', {
            defaultValue: 'Authorization timed out. Please try again.',
          })
        )
        setIsConnecting(false)
      }
    }, 2000) // Poll every 2 seconds
  }, [t])

  // Complete OAuth mutation
  const completeMutation = useMutation({
    mutationFn: async (account: CustomerAccountInfo) => {
      return completeGoogleAdsOAuth(account)
    },
    onSuccess: async (data) => {
      if (data.success) {
        // Mark connection complete to start sync status tracking
        markConnectionComplete()

        if (showSuccessToasts) {
          toast.success(
            t('cards.integrations.google.connected', {
              defaultValue: 'Google Ads connected successfully',
            })
          )
          toast.info(
            t('cards.integrations.syncStarted', {
              defaultValue: 'Syncing your ad data... This may take a few minutes.',
            }),
            { duration: 5000 }
          )
        }
        setShowAccountSelector(false)
        setAccounts([])
        setSelectedAccountId('')
        await queryClient.invalidateQueries({
          predicate: (query) =>
            Array.isArray(query.queryKey) &&
            query.queryKey.includes('integrations-status'),
        })
        await onConnected?.()
      } else {
        toast.error(
          data.message ||
            t('cards.integrations.google.error', {
              defaultValue: 'Failed to connect Google Ads',
            })
        )
      }
    },
    onError: (error: Error) => {
      toast.error(
        error.message ||
          t('cards.integrations.google.error', {
            defaultValue: 'Failed to connect Google Ads',
          })
      )
    },
  })

  const handleConnect = useCallback(async () => {
    if (!configQuery.data?.configured) {
      toast.error(
        t('cards.integrations.google.notConfigured', {
          defaultValue: 'Google Ads OAuth is not configured. Please contact support.',
        })
      )
      return
    }

    setIsConnecting(true)

    try {
      // Get authorization URL from backend
      const { authorizationUrl } = await getGoogleAdsAuthorizationUrl()

      // Calculate popup position (centered)
      const left = window.screenX + (window.outerWidth - POPUP_WIDTH) / 2
      const top = window.screenY + (window.outerHeight - POPUP_HEIGHT) / 2

      // Open popup
      popupRef.current = window.open(
        authorizationUrl,
        'google-ads-oauth',
        `width=${POPUP_WIDTH},height=${POPUP_HEIGHT},left=${left},top=${top},scrollbars=yes,resizable=yes`
      )

      if (!popupRef.current) {
        toast.error(
          t('cards.integrations.google.popupBlocked', {
            defaultValue: 'Popup was blocked. Please allow popups for this site.',
          })
        )
        setIsConnecting(false)
        return
      }

      // Start polling for accounts
      startPollingForAccounts()
    } catch (error) {
      toast.error(
        (error as Error).message ||
          t('cards.integrations.google.error', {
            defaultValue: 'Failed to start Google Ads authorization',
          })
      )
      setIsConnecting(false)
    }
  }, [configQuery.data?.configured, startPollingForAccounts, t])

  const handleSelectAccount = (): void => {
    if (!selectedAccountId) return
    const selectedAccount = accounts.find((a) => a.customerId === selectedAccountId)
    if (!selectedAccount) return
    completeMutation.mutate(selectedAccount)
  }

  const handleCancelAccountSelection = (): void => {
    setShowAccountSelector(false)
    setAccounts([])
    setSelectedAccountId('')
  }

  const isLoading = configQuery.isLoading || isConnecting
  const isDisabled = !activeOrgId || isLoading || !configQuery.data?.configured

  // Show syncing state if currently syncing
  if (isSyncing) {
    return (
      <Button
        type="button"
        variant="outline"
        size="sm"
        disabled
        className="gap-2"
      >
        <Loader2 className="size-3.5 animate-spin" />
        {t('cards.integrations.syncing', { defaultValue: 'Syncing...' })}
      </Button>
    )
  }

  return (
    <>
      <Button
        type="button"
        variant="outline"
        size="sm"
        onClick={handleConnect}
        disabled={isDisabled}
        className="gap-2"
      >
        {isLoading ? (
          <Loader2 className="size-3.5 animate-spin" />
        ) : (
          buttonIcon ?? <Plus className="size-3.5" />
        )}
        {isLoading
          ? t('cards.integrations.google.buttons.connecting', { defaultValue: 'Connecting...' })
          : buttonLabel ?? t('cards.integrations.google.buttons.connect', { defaultValue: 'Connect' })}
      </Button>

      {/* Account Selector Dialog */}
      <Dialog open={showAccountSelector} onOpenChange={setShowAccountSelector}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle>
              {t('cards.integrations.google.selectAccount.title', {
                defaultValue: 'Select Google Ads Account',
              })}
            </DialogTitle>
            <DialogDescription>
              {t('cards.integrations.google.selectAccount.description', {
                defaultValue: 'Choose which Google Ads account to connect for syncing ad spend data.',
              })}
            </DialogDescription>
          </DialogHeader>

          <div className="space-y-4 pt-4">
            <Select value={selectedAccountId} onValueChange={setSelectedAccountId}>
              <SelectTrigger>
                <SelectValue
                  placeholder={t('cards.integrations.google.selectAccount.placeholder', {
                    defaultValue: 'Select an account',
                  })}
                />
              </SelectTrigger>
              <SelectContent>
                {accounts.map((account) => (
                  <SelectItem key={account.customerId} value={account.customerId}>
                    {formatGoogleAdsId(account.customerId)}
                    {account.descriptiveName && ` ${account.descriptiveName}`}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>

            <div className="flex justify-end gap-2">
              <Button
                type="button"
                variant="outline"
                onClick={handleCancelAccountSelection}
              >
                {t('common.cancel', { defaultValue: 'Cancel' })}
              </Button>
              <Button
                type="button"
                onClick={handleSelectAccount}
                disabled={!selectedAccountId || completeMutation.isPending}
              >
                {completeMutation.isPending ? (
                  <>
                    <Loader2 className="size-4 mr-2 animate-spin" />
                    {t('cards.integrations.google.buttons.connecting', {
                      defaultValue: 'Connecting...',
                    })}
                  </>
                ) : (
                  t('cards.integrations.google.buttons.connect', { defaultValue: 'Connect' })
                )}
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </>
  )
}

export default GoogleAdsOAuthConnect
